import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Brain,
  BarChart,
  Monitor,
  TrendingUp,
  ShieldCheck,
  Gauge,
  DollarSign,
  Globe,
  ExternalLink,
} from "lucide-react";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";

const MotionLink = motion(Link);

// Service card — rendered as a real <a> (react-router Link) for crawlable internal links + SPA nav.
const ServiceCard = ({ service, index, t }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: index * 0.1,
      },
    },
  };

  const iconVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2 + index * 0.1,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
        delay: 0.3 + index * 0.1,
      },
    },
  };

  const IconComponent = service.icon;

  return (
    <MotionLink
      to={service.to}
      className="group p-8 bg-[#F5F7FA] rounded-xl hover:bg-[#0F1B2D] flex flex-col h-full min-h-[280px] w-full cursor-pointer relative no-underline"
      style={{ willChange: "transform, opacity" }}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      aria-label={`${service.title} — ${t("services.learnMore")}`}
    >
      <motion.div
        className="mb-4"
        variants={iconVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        aria-hidden="true"
      >
        <IconComponent
          size={48}
          className="text-[#C5A66A] group-hover:text-white"
          style={{ transition: "color 0.3s ease" }}
        />
      </motion.div>
      <motion.h3
        className="text-2xl font-bold text-[#0F1B2D] group-hover:text-white mb-4"
        style={{ transition: "color 0.3s ease" }}
        variants={contentVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        {service.title}
      </motion.h3>
      <motion.p
        className="text-gray-600 group-hover:text-gray-300 flex-grow mb-4"
        style={{ transition: "color 0.3s ease" }}
        variants={contentVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        {service.description}
      </motion.p>

      {/* Click indicator */}
      <div
        className="mt-4 flex items-center text-sm font-medium text-[#C5A66A] group-hover:text-white transition-colors duration-300"
        aria-hidden="true"
      >
        <ExternalLink size={16} className="mr-1" />
        {t("services.learnMore")}
      </div>

      {/* Subtle border indicator */}
      <div
        className="absolute inset-0 border-2 border-transparent group-hover:border-[#C5A66A] rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100"
        aria-hidden="true"
      ></div>
    </MotionLink>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useTranslation();

  const services = [
    {
      title: t("services.services.kiVertrieb.title"),
      description: t("services.services.kiVertrieb.description"),
      icon: TrendingUp,
      to: "/ki-vertrieb",
    },
    {
      title: t("services.services.kiVersicherung.title"),
      description: t("services.services.kiVersicherung.description"),
      icon: ShieldCheck,
      to: "/ki-versicherung",
    },
    {
      title: t("services.services.dashboards.title"),
      description: t("services.services.dashboards.description"),
      icon: Gauge,
      to: "/management-dashboards",
    },
    {
      title: t("services.services.ai.title"),
      description: t("services.services.ai.description"),
      icon: Brain,
      to: "/ai",
    },
    {
      title: t("services.services.strategy.title"),
      description: t("services.services.strategy.description"),
      icon: BarChart,
      to: "/strategie",
    },
    {
      title: t("services.services.digital.title"),
      description: t("services.services.digital.description"),
      icon: Monitor,
      to: "/digital",
    },
    {
      title: t("services.services.finance.title"),
      description: t("services.services.finance.description"),
      icon: DollarSign,
      to: "/finances",
    },
    {
      title: t("services.services.tax.title"),
      description: t("services.services.tax.description"),
      icon: Globe,
      to: "/taxes",
    },
  ];

  return (
    <section className="py-20 bg-white" aria-labelledby="services-title">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <motion.h2
            id="services-title"
            className="text-4xl lg:text-5xl font-bold text-[#0F1B2D] mb-6"
            variants={titleVariants}
          >
            {t("services.title")}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            variants={textVariants}
          >
            {t("services.subtitle")}
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          role="grid"
          aria-label="Services grid"
        >
          {services.map((service, index) => (
            <div
              key={service.title}
              role="gridcell"
              className="flex w-full h-full"
            >
              <ServiceCard
                service={service}
                index={index}
                t={t}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
